import type { Ref } from "react";

/**
 * The two kinds of marker a clinician can annotate a target's data graph with:
 * a protocol/phase transition, or an external event (medication, illness,
 * clinician change). The selection drives which fields the form shows and the
 * primary action label.
 */
export type MarkerType = "phaseChange" | "externalEvent";

/** A session the marker can be anchored to, shown in the Session dropdown. */
export interface MarkerSessionOption {
  /** Stable session identifier (session id or 1-based session number). */
  id: string;
  /** Human-readable label, e.g. "Session 24 — March 29th". */
  label: string;
}

/**
 * What a marker is positioned by on the graph: one of the target's recorded
 * sessions, or a specific calendar date.
 */
export type MarkerLinkMode = "session" | "date";

/** A goal and its non-archived targets, offered in the additional-targets picker. */
export interface MarkerGoalOption {
  id: number;
  title: string;
  targets: { id: number; title: string }[];
}

/**
 * Where the marker is being added from, and what else it can apply to. Without
 * it the dialog doesn't offer applying the marker to other targets.
 */
export interface MarkerTargetsContext {
  /** Client goals with their non-archived targets, the current goal included. */
  goalOptions: MarkerGoalOption[];
  /** Goal of the target the marker is added from. */
  currentGoalId: number;
  /** Target the marker is added from; it always applies to it. */
  currentTargetId: number;
}

/** Values captured by the marker form. `notes` is optional. */
export interface MarkerFormValues {
  type: MarkerType;
  /** Whether the marker is linked to a session or to a specific date. */
  linkMode: MarkerLinkMode;
  /** Session the marker is anchored to. Used when `linkMode` is "session". */
  sessionId: string;
  /**
   * Calendar date the marker is placed on, as the date picker holds it (local
   * midnight). Used when `linkMode` is "date".
   */
  markerDate: Date | null;
  /** Short display label shown on the graph (max 50 chars). */
  label: string;
  /** Optional longer description of the event. */
  notes: string;
  /** Also apply the marker to the other targets of the current goal. Date-linked only. */
  applyToCurrentGoal: boolean;
  /** Also apply the marker to targets picked from other goals. Date-linked only. */
  applyToOtherGoals: boolean;
  /** Targets picked from other goals; used while `applyToOtherGoals` is on. */
  otherTargetIds: number[];
  /**
   * Targets an edited marker already applies to that the checkboxes above can't
   * express — targets of the current goal when only some of them are covered,
   * which happens after a "this target only" delete. They're kept so saving an
   * unrelated change doesn't silently drop them.
   */
  extraTargetIds: number[];
  /**
   * Keeps the dialog open after a successful save so the clinician can add
   * another marker right away. External events only — phase lines don't offer this option.
   */
  createMore: boolean;
}

/**
 * Props shared by every marker-type variant. The dialog shell owns the form
 * state and hands each variant the current values plus a field setter, so the
 * variants stay presentational.
 */
export interface MarkerFieldsProps {
  /** Current form values. */
  values: MarkerFormValues;
  /** Sessions available to anchor the marker to. */
  sessions: MarkerSessionOption[];
  /** Updates a single form field. */
  onFieldChange: <K extends keyof MarkerFormValues>(key: K, value: MarkerFormValues[K]) => void;
  /** Focus target after a "create more" save, so the next entry starts typing. */
  labelInputRef?: Ref<HTMLInputElement>;
  /** Enables applying the marker to other targets. Omitted: not offered. */
  targetsContext?: MarkerTargetsContext;
}

/** Props shared by both dialog modes. */
export interface MarkerDialogBaseProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Called when the dialog is dismissed without saving. */
  onClose: () => void;
  /** Sessions available to anchor the marker to (oldest or newest first). */
  sessions: MarkerSessionOption[];
  /** Called with the captured values when the user confirms. */
  onSubmit: (values: MarkerFormValues) => void;
  /** Shows a spinner on the confirm button and disables all actions. */
  isLoading?: boolean;
  /**
   * Bumped by the caller after a successful save to clear the label and notes
   * without closing the dialog — the "create more" flow. Everything else (type,
   * session or date, the checkbox itself) is kept so the next marker is quick to
   * enter.
   */
  resetToken?: number;
  /**
   * Enables applying the marker to other targets of the client (date-linked
   * markers only). Omitted: the section isn't shown.
   */
  targetsContext?: MarkerTargetsContext;
}

/**
 * Create mode (default) starts from an empty form. Edit mode starts from
 * `initialValues` — required there, so an edit can't open blank — and locks the
 * marker type, the only field the API can't change.
 */
export type MarkerDialogProps = MarkerDialogBaseProps &
  (
    | { mode?: "create"; initialValues?: undefined }
    | { mode: "edit"; initialValues: MarkerFormValues }
  );

/** Max length of the marker label, enforced by the field and the backend. */
export const MARKER_LABEL_MAX = 50;
